class ModalElement extends HTMLElement {
  constructor() {
    super();
    this.triggerElement = null;
    this.closeButtons = [];
    this.backdrop = null;
    this.isOpen = false;
    this.CLOSE_EVENTS = ['click', 'keydown'];
    this.MOUSE_EVENTS = ['mousemove', 'mouseover', 'mouseenter'];
    this.openOnLoad = this.getAttribute('data-modal-open-on-load') === 'true';
  }

  connectedCallback() {
    // Move the modal to body if it's not already there
    if (![...document.body.children].includes(this)) {
      const originalId = this.id;
      document.body.appendChild(this);
      // Keep the original ID
      this.id = originalId;
    }

    this.init();
    this.setAttribute('aria-hidden', 'true');
  }

  init() {
    // Find modal elements within this custom element
    this.closeButtons = this.querySelectorAll('[data-modal-close]');
    this.backdrop = this.querySelector('[data-modal-backdrop]');

    this.setupEventListeners();

    // Create global functions for this specific modal
    this.createGlobalFunctions();
  }

  setupEventListeners() {
    // Close button event listeners
    this.closeButtons.forEach(button => {
      this.CLOSE_EVENTS.forEach(eventType => {
        button.addEventListener(eventType, () => {
          if (eventType === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
            return;
          }
          this.close();
        });
      });
    });

    // Backdrop click to close
    if (this.backdrop) {
      this.backdrop.addEventListener('click', (e) => {
        e.stopImmediatePropagation();
        e.preventDefault();
        this.close();
      });

      this.MOUSE_EVENTS.forEach((eventType) => {
        this.backdrop.addEventListener(eventType, (event) => {
          const closeButton = this.backdrop.querySelector('.modal__close');
          if (!closeButton) return;
          const x = event.clientX - (closeButton.offsetWidth / 2);
          const y = event.clientY - (closeButton.offsetHeight / 2);
          closeButton.style.cssText = `--_modal-cursor-x: ${x}px; --_modal-cursor-y: ${y}px;`;
        });
      });
    }

    // Escape key to close
    this.addEventListener('keydown', (e) => this.handleKeydown(e));

    // Check if there is a button with aria-controls pointing to this modal
    document.querySelectorAll(`[aria-controls="${this.id}"][data-modal-trigger]`).forEach(button => {
      this.CLOSE_EVENTS.forEach(eventType => {
        button.addEventListener(eventType, (e) => {
          if (eventType === 'keydown' && e.key !== 'Enter' && e.key !== ' ') {
            return;
          }
          e.preventDefault();
          const triggerType = button.getAttribute('data-modal-trigger');
          this.open(button, triggerType);
        });
      });
    });

    // Open on load if specified
    if (this.openOnLoad) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          this.open();
        }, 100);
      });
    }
  }

  handleKeydown(e) {
    if (!this.isOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.close();
      return;
    }

    // Trap focus within modal
    if (e.key === 'Tab') {
      this.trapFocus(e);
    }
  }

  trapFocus(e) {
    const focusableElements = this.getFocusableElements();

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Only trap focus at the boundaries - let browser handle navigation between elements
    if (e.shiftKey) {
      // Shift + Tab: moving backwards
      if (document.activeElement === firstFocusable ||
        document.activeElement === this ||
        !this.contains(document.activeElement)) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      // Tab: moving forwards
      if (document.activeElement === lastFocusable ||
        document.activeElement === this ||
        !this.contains(document.activeElement)) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }
  // Helper method to get all focusable elements consistently
  getFocusableElements() {
    const selector = 'button:not([disabled],[tabindex="-1"],[hidden]), [href]:not([hidden]), input:not([disabled],[hidden]), select:not([disabled],[hidden]), textarea:not([disabled],[hidden]), [tabindex]:not([tabindex="-1"],[disabled],[hidden]), details:not([disabled],[hidden]), summary:not([disabled],[hidden])';
    const elements = this.querySelectorAll(selector);

    // Filter out elements that are not actually focusable
    const focusableElements = Array.from(elements).filter(element => {
      // Check if element is visible and actually focusable
      const style = getComputedStyle(element);
      const isVisible = style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0';

      // Check if element is actually focusable (not programmatically disabled)
      const isFocusable = element.tabIndex >= 0 || element.tagName === 'BUTTON' || element.tagName === 'A' || element.tagName === 'INPUT' || element.tagName === 'SELECT' || element.tagName === 'TEXTAREA';

      return isVisible && isFocusable;
    });

    // If no focusable elements found, include the modal element itself if it has tabindex
    if (focusableElements.length === 0 && this.hasAttribute('tabindex')) {
      focusableElements.push(this);
    }

    return focusableElements;
  }

  open(trigger = null, triggerType = null) {
    if (this.isOpen) return;

    this.triggerElement = trigger || document.activeElement;
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.isOpen = true;

    this.setAttribute('tabindex', '1');

    // Multiple aggressive attempts to steal focus from trigger button
    const forceFocus = () => {
      // Force blur on trigger
      if (this.triggerElement && typeof this.triggerElement.blur === 'function') {
        this.triggerElement.blur();
      }

      // Force focus on modal immediately
      this.focus();

      // Also try focusing the first child element if available
      const focusableElements = this.getFocusableElements();
      if (focusableElements.length > 0) {
        const firstInput = focusableElements.find(el => el.tagName === 'INPUT' && el.classList.contains('need-focus'));
        const firstElement = firstInput || focusableElements.find(el => el !== this) || focusableElements[0];
        firstElement.focus();
      }

      // Verify focus moved - if not, keep trying
      if (document.activeElement === this.triggerElement) {
        // Try focusing container as fallback
        const container = this.querySelector('.modal__container');
        if (container) {
          container.setAttribute('tabindex', '-1');
          container.focus();
        }
      }
    };

    forceFocus();

    requestAnimationFrame(() => {
      setTimeout(() => {
        forceFocus();
      }, 100);
    });

    // Dispatch custom event with trigger type
    this.dispatchEvent(new CustomEvent('modal:open', {
      bubbles: true,
      detail: { modal: this, triggerType: triggerType }
    }));

    // Handle tablist activation if present
    if (triggerType) {
      setTimeout(() => {
        this.activateTabByTriggerType(triggerType);
      }, 150);
    }
  }

  activateTabByTriggerType(triggerType) {
    const tablist = this.querySelector('accessibility-tablist');
    if (!tablist) return;

    const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
    const modalContent = tablist.closest('[slot="modal-content"]') || this;
    const panels = Array.from(modalContent.querySelectorAll('[role="tabpanel"]'));

    if (tabs.length === 0 || panels.length === 0) return;

    let targetIndex = 0;

    if (triggerType) {
      targetIndex = tabs.findIndex((tab) => {
        const controls = tab.getAttribute('aria-controls');
        return controls && controls.includes(`-tab-${triggerType}`);
      });
      if (targetIndex === -1) targetIndex = 0;
    }

    tabs.forEach((tab, idx) => {
      const isActive = idx === targetIndex;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach((panel, idx) => {
      const isActive = idx === targetIndex;
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      panel.style.display = isActive ? '' : 'none';
    });
  }

  close() {
    if (!this.isOpen) return;

    this.setAttribute('aria-hidden', 'true');
    this.isOpen = false;

    // Only restore body overflow if no other modals are open
    const openModals = document.querySelectorAll('modal-element[aria-hidden="false"]');
    if (openModals.length === 0) {
      document.body.style.overflow = '';
    }
    this.setAttribute('tabindex', '-1');

    // Return focus to trigger element
    if (this.triggerElement) {
      // Use requestAnimationFrame to ensure the modal is hidden before focusing
      requestAnimationFrame(() => {
        if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
          this.triggerElement.focus();
        }
        this.triggerElement = null;
      });
    }

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('modal:close', {
      bubbles: true,
      detail: { modal: this }
    }));
  }

  toggle(trigger = null, triggerType = null) {
    if (this.isOpen) {
      this.close();
    } else {
      this.open(trigger, triggerType);
    }
  }

  createGlobalFunctions() {
    const modalId = this.id;
    if (modalId) {
      // Create global functions for backward compatibility
      window[`open_${modalId}`] = (trigger, triggerType) => this.open(trigger, triggerType);
      window[`close_${modalId}`] = () => this.close();
      window[`toggle_${modalId}`] = (trigger, triggerType) => this.toggle(trigger, triggerType);
    }
  }

  // Public methods for external access
  isModalOpen() {
    return this.isOpen;
  }

  setTitle(title) {
    const titleElement = this.querySelector('.modal__title');
    if (titleElement) {
      titleElement.textContent = title;
    }
  }

  setContent(content) {
    const bodyElement = this.querySelector('.modal__body');
    if (bodyElement) {
      bodyElement.innerHTML = content;
    }
  }

  // Static utility methods for managing all modals
  static open(modalId, trigger = null, triggerType = null) {
    const modal = document.getElementById(modalId);
    if (modal && modal.open) {
      modal.open(trigger, triggerType);
    }
  }

  static close(modalId) {
    const modal = document.getElementById(modalId);
    if (modal && modal.close) {
      modal.close();
    }
  }

  static closeAll() {
    const modals = document.querySelectorAll('modal-element[aria-hidden="false"]');
    modals.forEach(modal => modal.close());
  }

  static getOpenModals() {
    return document.querySelectorAll('modal-element[aria-hidden="false"]');
  }

  static toggle(modalId, trigger = null, triggerType = null) {
    const modal = document.getElementById(modalId);
    if (modal && modal.toggle) {
      modal.toggle(trigger, triggerType);
    }
  }
}

if (!customElements.get('modal-element')) {
  // Register the custom element
  customElements.define('modal-element', ModalElement);

  // Global escape key handler for all modals
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      const openModals = ModalElement.getOpenModals();
      if (openModals.length > 0) {
        // Close the last opened modal (top modal in stack)
        const lastModal = openModals[openModals.length - 1];
        if (lastModal.close) {
          e.preventDefault();
          e.stopPropagation();
          lastModal.close();
        }
      }
    }
  });
}

export default ModalElement;
